import React from 'react';
import { Link, useParams } from 'react-router-dom';
import usePrices from '../../hooks/usePrices';
import { SITE, formatMad } from '../../data/servers';
import { PAGES, gamePath, serverPath, buyPath, sellPath, gameLabel, serverInGame } from '../../site/routes';
import {
  usePageSeo, Breadcrumbs, PageHeader, Section, ComparisonTable, LinkCards, CtaBand, ArrowLink, TextLink
} from '../../site/ui';
import NotFoundPage from './NotFoundPage';
import CoinLoader from '../../components/CoinLoader';

const EXAMPLE_MILLIONS = 10;

export default function ServerPage({ game }) {
  const { slug } = useParams();
  const { servers, updatedAt, ready } = usePrices();
  const server = serverInGame(servers, game, slug);

  const label = gameLabel(game);
  const breadcrumbs = server ? [
    { label: 'Accueil', to: PAGES.home },
    { label: 'Serveurs Dofus', to: PAGES.servers },
    { label: `Kamas ${label}`, to: gamePath(game) },
    { label: `Kamas ${server.name}`, to: serverPath(server) },
  ] : null;

  usePageSeo(server ? {
    title: `Kamas ${server.name} Maroc : achat ${formatMad(server.buy)}, vente ${formatMad(server.sell)} | AV Kamas`,
    description: `Achat et vente de Kamas ${server.name} (${label}) au Maroc : ${formatMad(server.buy)} le million à l'achat, ${formatMad(server.sell)} à la vente. Paiement en DH par virement.`,
    path: serverPath(server),
    breadcrumbs,
    jsonLd: [{
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: `Kamas ${server.name} (${label})`,
      description: server.description,
      category: `Kamas ${label}`,
      offers: {
        '@type': 'Offer',
        price: server.buy.toFixed(2),
        priceCurrency: 'MAD',
        availability: 'https://schema.org/InStock',
        url: `${SITE.url}${serverPath(server)}`,
        seller: { '@type': 'Organization', name: SITE.name },
      },
    }],
  } : { title: 'Page introuvable | AV Kamas', path: PAGES.home, noindex: true });

  if (!server) {
    // Tant que le catalogue n'est pas chargé, on attend : un serveur ajouté
    // depuis l'admin n'apparaît qu'après la réponse de /api/prices.
    return ready ? <NotFoundPage embedded /> : <CoinLoader fullPage label="Chargement des prix…" />;
  }

  const related = servers.filter(s => s.game === game && s.slug !== server.slug);
  const saving = server.competitor ? server.competitor.buy - server.buy : null;
  const bonus = server.competitor ? server.sell - server.competitor.sell : null;

  return (
    <main className="bg-gray-950 text-white">
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        kicker={`${label} · ${server.type}`}
        title={<>Kamas {server.name} : <span className="text-yellow-400">achat et vente au Maroc</span></>}
        intro={server.description}
      />

      <Section className="pt-0 md:pt-0">
        <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 max-w-3xl">
          <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-4 sm:p-5">
            <p className="text-sm text-green-400">Vous achetez 1M de Kamas {server.name}</p>
            <p className="text-3xl sm:text-4xl font-bold text-white my-1">{formatMad(server.buy)}</p>
            <p className="text-xs sm:text-sm text-gray-400 mb-3">{EXAMPLE_MILLIONS}M = {formatMad(server.buy * EXAMPLE_MILLIONS)}</p>
            <Link to={buyPath(server)} className="block py-3 text-center text-sm sm:text-base font-bold rounded-xl bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-black">
              Acheter des Kamas {server.name}
            </Link>
          </div>
          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 sm:p-5">
            <p className="text-sm text-red-400">Vous vendez 1M de Kamas {server.name}</p>
            <p className="text-3xl sm:text-4xl font-bold text-white my-1">{formatMad(server.sell)}</p>
            <p className="text-xs sm:text-sm text-gray-400 mb-3">{EXAMPLE_MILLIONS}M = {formatMad(server.sell * EXAMPLE_MILLIONS)}</p>
            <Link to={sellPath(server)} className="block py-3 text-center text-sm sm:text-base font-bold rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white">
              Vendre des Kamas {server.name}
            </Link>
          </div>
        </div>
        <p className="text-gray-500 text-xs sm:text-sm mt-3">
          {updatedAt ? `Prix mis à jour le ${new Date(updatedAt).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}. ` : ''}
          Prix en dirhams pour 1 million de Kamas.
        </p>
      </Section>

      <Section className="bg-gray-900/40">
        <div className="grid md:grid-cols-2 gap-6 md:gap-10 text-sm sm:text-base text-gray-300 leading-relaxed">
          <div>
            <h2 className="text-lg sm:text-2xl font-bold text-white mb-2">Acheter des Kamas {server.name}</h2>
            <p className="mb-3">
              Indiquez votre personnage sur {server.name}, la quantité voulue, puis payez en dirhams par virement depuis votre banque
              marocaine. Pour {EXAMPLE_MILLIONS}M de Kamas {server.name}, comptez {formatMad(server.buy * EXAMPLE_MILLIONS)}.
              Toutes les étapes sont détaillées dans notre guide <TextLink to={PAGES.buyGuide}>achat Kamas Maroc</TextLink>.
            </p>
            <ArrowLink to={buyPath(server)}>Commander des Kamas {server.name}</ArrowLink>
          </div>
          <div>
            <h2 className="text-lg sm:text-2xl font-bold text-white mb-2">Vendre des Kamas {server.name}</h2>
            <p className="mb-3">
              Vous remettez les Kamas en jeu sur {server.name} et recevez le virement en DH sur votre RIB. Pour {EXAMPLE_MILLIONS}M
              de Kamas {server.name}, vous touchez {formatMad(server.sell * EXAMPLE_MILLIONS)}. Voir le guide{' '}
              <TextLink to={PAGES.sellGuide}>vente Kamas Maroc</TextLink>.
            </p>
            <ArrowLink to={sellPath(server)}>Vendre mes Kamas {server.name}</ArrowLink>
          </div>
        </div>
      </Section>

      <Section
        title={`Kamas ${server.name} : AV Kamas vs TryAndJudge`}
        intro={saving !== null
          ? `Sur ${server.name}, vous payez ${formatMad(saving)} de moins par million à l'achat et recevez ${formatMad(bonus)} de plus par million à la vente qu'avec TryAndJudge.`
          : undefined}
      >
        <ComparisonTable servers={[server]} updatedAt={updatedAt} />
        <div className="mt-4"><ArrowLink to={PAGES.comparison}>Comparatif complet des 20 serveurs</ArrowLink></div>
      </Section>

      <Section title={`Autres serveurs ${label}`} className="bg-gray-900/40">
        <LinkCards
          columns="grid-cols-2 lg:grid-cols-4"
          links={related.map(s => ({ to: serverPath(s), title: `Kamas ${s.name}`, text: `Achat ${formatMad(s.buy)} · Vente ${formatMad(s.sell)} · ${s.type}` }))}
        />
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
          <ArrowLink to={gamePath(game)}>Tous les Kamas {label}</ArrowLink>
          <ArrowLink to={PAGES.prices}>Tous les prix des Kamas</ArrowLink>
        </div>
      </Section>

      <CtaBand
        title={`Acheter ou vendre des Kamas ${server.name} ?`}
        text="Payez ou soyez payé en dirhams, suivez tout dans le chat."
        buyTo={buyPath(server)}
        sellTo={sellPath(server)}
      />
    </main>
  );
}
